<script setup>
import { computed, ref } from 'vue';
import { useEventListener } from '@vueuse/core';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  activeTab: {
    type: String,
    default: wootConstants.ASSIGNEE_TYPE.ME,
  },
});

const emit = defineEmits(['chatTabChange']);

const activeTabIndex = computed(() => {
  return props.items.findIndex(item => item.key === props.activeTab);
});

const onTabChange = selectedTabIndex => {
  const selectedItem = props.items[selectedTabIndex];
  if (selectedItem && selectedItem.key !== props.activeTab) {
    emit('chatTabChange', selectedItem.key);
  }
};

// --- Scroll orizzontale: rotellina del mouse + trascinamento (grab & drag) ---
const scrollerRef = ref(null);
const isDragging = ref(false);
let dragStartX = 0;
let dragStartScroll = 0;
let dragMoved = false;

const onWheel = event => {
  const el = scrollerRef.value;
  if (!el || el.scrollWidth <= el.clientWidth) return;
  el.scrollLeft += event.deltaY;
  event.preventDefault();
};

const onDragStart = event => {
  const el = scrollerRef.value;
  if (!el || el.scrollWidth <= el.clientWidth) return; // niente overflow, niente drag
  isDragging.value = true;
  dragMoved = false;
  dragStartX = event.pageX;
  dragStartScroll = el.scrollLeft;
  event.preventDefault(); // evita la selezione del testo durante il trascinamento
};

useEventListener(window, 'mousemove', event => {
  if (!isDragging.value || !scrollerRef.value) return;
  const delta = event.pageX - dragStartX;
  if (Math.abs(delta) > 3) dragMoved = true;
  scrollerRef.value.scrollLeft = dragStartScroll - delta;
});

useEventListener(window, 'mouseup', () => {
  isDragging.value = false;
});

// Se è stato un trascinamento (non un click secco) non cambiare filtro.
const onTabClick = index => {
  if (dragMoved) {
    dragMoved = false;
    return;
  }
  onTabChange(index);
};

const keyboardEvents = {
  'Alt+KeyN': {
    action: () => {
      if (props.activeTab === wootConstants.ASSIGNEE_TYPE.ALL) {
        onTabChange(0);
      } else {
        const nextIndex = (activeTabIndex.value + 1) % props.items.length;
        onTabChange(nextIndex);
      }
    },
  },
};

useKeyboardEvents(keyboardEvents);
</script>

<template>
  <!-- Filtri a pill compatte su una riga scorrevole. Si scorre con la rotellina del mouse
       e trascinando (grab & drag); un click secco seleziona comunque il filtro. -->
  <div
    ref="scrollerRef"
    class="flex items-center gap-1 px-3 py-0.5 overflow-x-auto no-scrollbar select-none"
    :class="isDragging ? 'cursor-grabbing' : 'cursor-grab'"
    @wheel="onWheel"
    @mousedown="onDragStart"
  >
    <button
      v-for="(item, index) in items"
      :key="item.key"
      type="button"
      class="inline-flex items-center gap-1 h-6 px-2 rounded-full text-xs font-medium whitespace-nowrap shrink-0 transition-colors cursor-[inherit]"
      :class="
        item.key === activeTab
          ? 'bg-n-brand/15 text-n-brand'
          : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
      "
      @click="onTabClick(index)"
    >
      <span>{{ item.name }}</span>
      <span
        v-if="item.count !== undefined && item.count !== null"
        class="text-[0.625rem] tabular-nums"
        :class="item.key === activeTab ? 'text-n-brand/80' : 'text-n-slate-9'"
      >
        {{ item.count }}
      </span>
    </button>
  </div>
</template>
